import { useRef, useState, type JSX } from 'react';
import type { Id } from '../../../core/model';
import { Slider } from '../../components/Slider';
import { useRuntime, useStudio } from '../../context';
import { formatDuration } from '../../format';
import { safely } from '../../safe';

/** The position bar of a media source: it follows the playback and seeks where it is dropped. Nothing without a known duration. */
export function MediaSeek({ sourceId }: { sourceId: Id }): JSX.Element | null {
  const studio = useStudio();
  const media = useRuntime((r) => r.sources[sourceId]?.media);
  // While the position bar is dragged it shows the pointer; the seek is sent once, on release.
  const [scrub, setScrub] = useState<number | null>(null);
  // Read on release: a keyboard step changes and commits in one event, before the state updates.
  const scrubbed = useRef<number | null>(null);
  if (!media || media.durationMs <= 0) return null;
  return (
    <Slider
      label="Playback position"
      value={scrub ?? media.timeMs}
      min={0}
      max={media.durationMs}
      step={100}
      bigStep={5000}
      valueText={formatDuration(scrub ?? media.timeMs)}
      onChange={(ms) => {
        scrubbed.current = ms;
        setScrub(ms);
      }}
      onCommit={() => {
        // Seek takes milliseconds, like MediaState.timeMs.
        if (scrubbed.current !== null) void safely(studio, () => studio.invokeSource(sourceId, 'seek', Math.round(scrubbed.current!)), 'The source did not respond');
        scrubbed.current = null;
        setScrub(null);
      }}
    />
  );
}

/**
 * Transport for sources with `caps.controllableMedia`, bound to the runtime's
 * media state. Entry buttons appear only for sources that hold several items.
 */
export function MediaTransport({ sourceId }: { sourceId: Id }): JSX.Element {
  const studio = useStudio();
  const media = useRuntime((r) => r.sources[sourceId]?.media);
  const run = (action: string, args?: unknown): void => void safely(studio, () => studio.invokeSource(sourceId, action, args), 'The source did not respond');
  const playing = media?.state === 'playing' || media?.state === 'buffering';
  const several = (media?.count ?? 0) > 1;
  const button = (action: string, label: string): JSX.Element => (
    <button type="button" className="ui-btn" onClick={() => run(action)}>
      {label}
    </button>
  );
  return (
    <div className="media-transport ui-row" role="group" aria-label="Media controls">
      {several ? button('previous', 'Previous') : null}
      {playing ? button('pause', 'Pause') : button('play', 'Play')}
      {button('stop', 'Stop')}
      {button('restart', 'Restart')}
      {several ? button('next', 'Next') : null}
      {media && media.durationMs > 0 ? (
        <div className="media-seek">
          <MediaSeek sourceId={sourceId} />
        </div>
      ) : null}
      <span className="ui-mono ui-dim media-position" role="status" aria-label="Playback position">
        {media ? `${formatDuration(media.timeMs)}${media.durationMs > 0 ? ` / ${formatDuration(media.durationMs)}` : ''}` : '—'}
        {media && several ? ` · item ${(media.index ?? 0) + 1} of ${media.count}` : ''}
        {media ? ` · ${media.state}` : ''}
      </span>
    </div>
  );
}
